import { ref, useContext, useRoute, computed, defineComponent, getCurrentInstance, useCssModule, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { presets } from '~/composables/grid/brandvoice';
import { useDuck } from '~/services';
import { cachePage, useAsyncRef, createHead } from '~/utils';
import { createGridChunks, getArticlesSize } from '~/composables';
const duck = useDuck();
const columns = {
    desktop: 2,
    desktopMedium: 2,
    desktopWide: 3,
    mobile: 1,
    mobileMedium: 1,
    mobileWide: 1,
    tablet: 2,
};
export default defineComponent({
    name: 'PageVoice',
    setup() {
        /**
         * Composables
         */
        const context = useContext();
        const css = useCssModule();
        const instance = getCurrentInstance();
        const route = useRoute();
        const viewport = context.$viewport;
        /**
         * Page offset
         */
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        /**
         * Articles.
         */
        const tax = useAsyncRef(instance, 'tax');
        /**
         * BaseImage bind attrs
         */
        const baseImage = computed(() => {
            const { field_file: svg, field_logo: image, name } = tax.value?.fields || {};
            const attrs = {
                alt: name,
                isBlurhashEnabled: false,
            };
            if (svg?.value) {
                return {
                    ...attrs,
                    image: svg.value,
                    shouldCrop: false,
                    shouldUseWebp: false,
                };
            }
            if (image?.value) {
                return {
                    ...attrs,
                    image: image.value,
                    cropMethod: 'contain',
                };
            }
            return false;
        });
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(tax.value.articles, articlesCards.value));
        /**
         * Is done.
         */
        const isDone = useAsyncRef(instance, 'isDone');
        const currentTabMobile = ref({ title: '' });
        const shouldShowObserver = computed(() => {
            if (viewport.isLessThan('tablet')) {
                return currentTabMobile.value.title === 'Статьи' && !isDone.value;
            }
            return !isDone.value;
        });
        const fetchArticlesTask = useTask(function* () {
            try {
                const path = route.value.path.replace(/^\/|\/$/g, '');
                const { data } = yield duck.pub.getPubTax({
                    'list[limit]': articlesCards.value,
                    'list[offset]': tax?.value?.articles?.length || 0 * (pageOffset.value + 1),
                    'tax[url]': path,
                });
                if (data.articles?.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!data.articles?.length) {
                    return;
                }
                tax.value.articles = [...tax.value.articles, ...data.articles];
            }
            catch (error) {
                console.error(error);
                isDone.value = true;
            }
        });
        function handleChangeTabMobile(tab) {
            currentTabMobile.value = tab;
        }
        function handleIntersect() {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            fetchArticlesTask.perform();
        }
        return {
            baseImage,
            css,
            currentTabMobile,
            grids,
            handleChangeTabMobile,
            handleIntersect,
            isDone,
            shouldShowObserver,
            tax,
        };
    },
    async asyncData(context) {
        const { app, env, error, req, res, route } = context;
        try {
            let isDone = false;
            const limit = getArticlesSize(app.$viewport.breakpoint, {
                columns,
                presets,
            });
            const pageNumber = Number(route.query.page) || 0;
            const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
            const path = route.path.replace(/^\/|\/$/g, '');
            const { data: tax } = await duck.pub.getPubTax({
                'list[limit]': limit,
                'list[offset]': limit * pageOffset,
                'tax[url]': path,
            });
            if (pageNumber && !tax.articles?.length) {
                throw new Error('Not Found');
            }
            if ((tax.articles || [])?.length < limit) {
                isDone = true;
            }
            let { description } = tax;
            if (!description) {
                // description = `${tax.name} – Brandvoice – Forbes Russia – одно из наиболее авторитетных и известных финансово-экономических изданий в мире`
                description = `${tax.name} – Блоги – Forbes Russia – одно из наиболее авторитетных и известных финансово-экономических изданий в мире`;
            }
            description = description?.slice(0, 250);
            const title = tax.name + '. Новости и статьи';
            const head = {
                canonical: env.HOST + route.path,
                description,
                title,
                url: env.HOST + route.path,
            };
            context.$page = app.$page = {
                title,
            };
            if (process.server) {
                const [firstArticle] = tax.articles || [];
                if (firstArticle) {
                    const date = firstArticle.changed || firstArticle.time || firstArticle.created;
                    cachePage({ date, req, res });
                }
            }
            return {
                $page: context.$page,
                head,
                isDone,
                pageOffset,
                tax,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head);
    },
});
